import { Component, OnInit, Input, ViewChild, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MATERIAL_IMPORTS } from '../../../material/material-imports';
import { SpinnerComponent } from '../../../shared/spinner/spinner.component';
import { CkEditorComponent } from '../../../shared/ck-editor/ck-editor.component';
import { ProblemAutoCompleteComponent } from '../problem-auto-complete/problem-auto-complete.component';
import { SortByPipe } from '../../../shared/pipes/sort-by.pipe';
import { Router, ActivatedRoute } from '@angular/router';
import { MatPaginator, PageEvent } from '@angular/material/paginator';

import { ProblemModel, SubjectModel } from '@generativ/much-smarter-models';
import { Observable, of } from 'rxjs';

import { ProblemService } from '../../../shared/problem.service';
import { AlertService } from '../../../core/alert/alert.service';

@Component({
  standalone: true,
  imports: [CommonModule, ...MATERIAL_IMPORTS, SpinnerComponent, CkEditorComponent, ProblemAutoCompleteComponent],
  selector: 'app-problem-list',
  templateUrl: './problem-list.component.html',
  styleUrls: ['./problem-list.component.scss'],
  providers: [SortByPipe],
})
export class ProblemListComponent implements OnInit {

  @Input() problems: ProblemModel[];
  @Input() showPagination = true;
  readonly paginatedProblems = signal<ProblemModel[]>([]);
  readonly loading = signal(true);
  // MatPaginator Inputs
  @ViewChild(MatPaginator) paginator: MatPaginator;
  length = 100;
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 25, 100];

  searchOffset = 0;
  searchLimit = 10;
  searchTotalProblems = 0;
  searchQuery = null;
  searchProblems: ProblemModel[];

  ready = false;


  constructor(
    private problemService: ProblemService,
    private alertService: AlertService,
    private router: Router,
    private route: ActivatedRoute,
    private sortBy: SortByPipe
  ) {
  }

  ngOnInit() {
    if (this.problems && !this.showPagination) {
      this.paginatedProblems.set(this.sortBy.transform(this.problems, 'title'));
      this.length = this.problems.length;
      this.loading.set(false);
    } else if (this.problems) {
      this.paginatedProblems.set(this.sortBy.transform(this.problems, 'title').slice(0, this.pageSize));
      this.length = this.problems.length;
      this.loading.set(false);
    } else {
      this.problemService.getProblemsWithPagination(0, this.pageSize, 'title').subscribe({
        next: (problemPaginator) => {
          this.paginatedProblems.set(problemPaginator.problems);
          this.length = problemPaginator.totalCount;
          this.loading.set(false);
        },
        error: (error) => {
          this.alertService.error(error);
          this.length = 0;
          this.loading.set(false);
        },
      });
    }
  }

  getProblemsPagination(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;

    const offset = this.pageIndex * this.pageSize;
    this.problemService.getProblemsWithPagination(offset, this.pageSize, 'title').subscribe({
      next: (problemPaginator) => {
        this.paginatedProblems.set(problemPaginator.problems);
        this.length = problemPaginator.totalCount;
      },
      error: (error) => {
        this.alertService.error(error);
      },
    });
  }


  /**
   * Navigate to  Create problem.
   */
  onCreateProblem() {
    this.router.navigate(['../problems/create'], { relativeTo: this.route });
  }

  /**
   * Navigate to  Manage problem.
   */
  onManageProblem(id: number) {
    this.router.navigate(['/problems/' + id + '/manage']);
  }

  /**
   * Navigate to  Edit problem.
   */
  onEditProblem(id: number) {
    this.router.navigate(['/problems/' + id + '/edit']);
  }

  navigateToProblemManage(problem: ProblemModel) {
    this.onManageProblem(problem.id);
  }

  /**
   * Load more problems with the selected search query
   */
  loadMoreProblems() {
    this.searchOffset = this.searchOffset + this.searchLimit;
    this.getProblems(this.searchOffset, this.searchLimit);
  }

  /**
   * Hits api when search query changes
   * @param searchQuery
   */
  handleQueryChange(searchQuery: string) {
    this.searchQuery = searchQuery;
    this.getProblems(this.searchOffset, this.searchLimit);
  }

  private getProblems(offset: number, limit: number) {
    // Get the first 100 problems sort by title
    this.problemService.getProblemsWithPagination(
      offset,
      limit,
      'title',
      this.searchQuery
    ).subscribe((problemsPaginator) => {
      this.searchProblems = problemsPaginator.problems;
      this.ready = true;
      // Once we have the first 100 problems filter the problems that currently are in the
      this.searchTotalProblems = problemsPaginator.totalCount;
    });
  }

  /**
   * Returns true if there're more problems to load
   */
  hasMore(): boolean {
    return this.searchTotalProblems >= this.pageIndex + this.pageSize;
  }

}

